import React, { useEffect, useMemo, useRef, useState } from "react";
import CssBaseline from "@mui/material/CssBaseline";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { tokens } from "../../../theme";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  Box,
  Chip,
} from "@mui/material";
import Loading from "../../../components/loading";
import Error from "../../../components/Error";
import AlertDialog from "../../../components/alertDialog";
import { toast } from "react-toastify";
import AppBarSearch from "../../../global/appBarSearch";
import NoDataTable from "../../../components/noDataTable";
import { useRestaurant } from "../../../hooks/RestaurantContext";
import {
  deleteCoupon,
  fetchCoupons,
  getCouponsError,
  getCouponsStatus,
  getCouponsSuccess,
  selectAllCoupons,
  toggleCoupon,
  updateStatus,
} from "../../../features/couponSlice";
import { formatDateTime } from "../../../global/functions/dateFormat";
import {
  CouponStatusFilterList,
  CouponTypeList,
  DaysOfWeekOptions,
  TargetType,
  TargetTypeOptions,
  formatRecurringDaysForDisplay,
  getCouponLabel,
  getCouponStatusKey,
  getCouponStatusMeta,
} from "../../../models/coupon.model";
import FiltersDialog, {
  FiltersButton,
} from "../../../components/filters/filtersDialog";
import FilterChipGroup from "../../../components/filters/filterChipGroup";
import { CouponSortList, applySort } from "../../../models/sort.model";
import ParagraphHighliter from "../../../components/paragraphHighliter";
import { ResponsiveProvider } from "../../../hooks/uiHook";
import ShowMultiItems from "../../../components/showMultiItems";
import { useTranslation } from "react-i18next";
import TableActions from "../../../components/tableActions";

// Only the real target-type options (Catégories/Produits/Catégories+Produits)
// go into the Catégorie cible chip group — the list's own "Tous" entry
// (TargetType.ALL) is intentionally left out because FilterChipGroup's
// `allowAll` already provides an equivalent "no filter" chip, and showing
// both would render two identically-labeled "Tout" chips.
const CategoryTypeFilterOptions = TargetTypeOptions.filter(
  (option) => option._id !== TargetType.ALL
).map((option) => ({
  _id: option._id,
  label: option.name,
  labelKey: option.labelKey,
}));

const RecurringDaysFilterOptions = DaysOfWeekOptions.map((day) => ({
  _id: day._id,
  label: day.name,
  labelKey: day.labelKey,
}));
const CouponManagement = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { restaurantId } = useRestaurant();
  const [localCoupons, setLocalCoupons] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [cardId, setCardId] = useState(null);
  const [couponStatusFilter, setCouponStatusFilter] = useState("");
  const [couponTypeFilter, setCouponTypeFilter] = useState("");
  const [categoryTypeFilter, setCategoryTypeFilter] = useState("");
  const [recurringDaysFilter, setRecurringDaysFilter] = useState([]);
  const [sortFilter, setSortFilter] = useState("");
  const [filtersDialogOpen, setFiltersDialogOpen] = useState(false);
  // Draft copies, bound to the FiltersDialog controls only: nothing here
  // affects the list until Appliquer commits it into the state above.
  const [draftCouponStatusFilter, setDraftCouponStatusFilter] = useState("");
  const [draftCouponTypeFilter, setDraftCouponTypeFilter] = useState("");
  const [draftCategoryTypeFilter, setDraftCategoryTypeFilter] = useState("");
  const [draftRecurringDaysFilter, setDraftRecurringDaysFilter] = useState([]);
  const [draftSortFilter, setDraftSortFilter] = useState("");
  const [active, setActive] = useState(false);
  const [activeType, setActiveType] = useState(false);
  const status = useSelector(getCouponsStatus);
  const error = useSelector(getCouponsError);
  const coupons = useSelector(selectAllCoupons);
  const success = useSelector(getCouponsSuccess);

  const handleClickOpen = (cardId) => {
    setOpen(true);
    setCardId(cardId);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    navigate("add");
  };

  const handleModify = (data) => {
    navigate("modify", { state: { coupon: data } });
  };

  const hasFetchedInMountRef = useRef(false);
  const lastFetchedRestaurantRef = useRef(restaurantId);
  useEffect(() => {
    const restaurantChanged =
      hasFetchedInMountRef.current &&
      lastFetchedRestaurantRef.current !== restaurantId;
    const alreadyLoaded = coupons.length > 0 && status === "fetchData";
    if (restaurantChanged || !alreadyLoaded) {
      dispatch(fetchCoupons(restaurantId));
    }
    hasFetchedInMountRef.current = true;
    lastFetchedRestaurantRef.current = restaurantId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, restaurantId]);

  useEffect(() => {
    if (status === "fetchData") setLocalCoupons(coupons);
  }, [coupons, status]);

  // Order-insensitive array compare for recurringDaysFilter/
  // draftRecurringDaysFilter (chip toggling can produce the same set of
  // days in a different order).
  const arraysEqualUnordered = (a, b) => {
    const sortedA = [...(a || [])].map(String).sort();
    const sortedB = [...(b || [])].map(String).sort();
    return JSON.stringify(sortedA) === JSON.stringify(sortedB);
  };

  // Live draft-vs-applied diff: Appliquer stays disabled until at least one
  // draft filter differs from what's currently applied.
  const hasPendingChanges =
    draftCouponStatusFilter !== couponStatusFilter ||
    draftCouponTypeFilter !== couponTypeFilter ||
    draftCategoryTypeFilter !== categoryTypeFilter ||
    !arraysEqualUnordered(draftRecurringDaysFilter, recurringDaysFilter) ||
    draftSortFilter !== sortFilter;

  const filteredCouponsMemo = useMemo(() => {
    let filtered = localCoupons || [];

    if (search) {
      filtered = filtered.filter((coupon) =>
        (coupon.code || "").toLowerCase().includes(search.toLowerCase())
      );
    }

    if (couponStatusFilter !== "") {
      filtered = filtered.filter(
        (coupon) => getCouponStatusKey(coupon) === couponStatusFilter
      );
    }

    if (couponTypeFilter !== "") {
      filtered = filtered.filter(
        (coupon) => String(coupon.couponType) === String(couponTypeFilter)
      );
    }

    if (categoryTypeFilter !== "") {
      filtered = filtered.filter(
        (coupon) => String(coupon.categoryType) === String(categoryTypeFilter)
      );
    }

    if (recurringDaysFilter.length > 0) {
      filtered = filtered.filter((coupon) => {
        const days = coupon.recurringDays || [];
        // No recurringDays restriction (empty/missing array) means the
        // coupon runs every day — same interpretation the table already
        // uses (it renders nothing/no day chips in that case) — so it
        // matches any day selection.
        if (days.length === 0) return true;
        return days.some((day) =>
          recurringDaysFilter.map(Number).includes(Number(day))
        );
      });
    }

    return applySort(filtered, sortFilter, {
      getPrice: (coupon) => coupon.couponValue,
      getUsage: (coupon) => coupon.usageCount,
      getDate: (coupon) => coupon.startDate,
    });
  }, [
    localCoupons,
    search,
    couponStatusFilter,
    couponTypeFilter,
    categoryTypeFilter,
    recurringDaysFilter,
    sortFilter,
  ]);

  useEffect(() => {
    if (status === "deleteSuccess") {
      setOpen(false);
      toast.success(success);
      setLocalCoupons((prev) => prev.filter((item) => item._id !== cardId));
      dispatch(updateStatus());
      setCardId("");
    } else if (status === "deleteError") {
      toast.error(error);
      setCardId("");
    }

    if (status === "toggleSuccess") {
      toast.success(success);
      setLocalCoupons((prev) =>
        prev.map((item) =>
          item._id === cardId ? { ...item, isActive: !item.isActive } : item
        )
      );
      dispatch(updateStatus());
      setCardId("");
    } else if (status === "toggleError") {
      toast.error(error);
      setCardId("");
    }
  }, [cardId, dispatch, error, status, success]);

  return (
    <div className="main-application">
      <CssBaseline />
      <ResponsiveProvider maxWidth={900}>
        <AppBarSearch
          disableMobileDrawer
          searchText={t("search.byCode")}
          handleSubmit={handleSubmit}
          handleSearch={(e) => setSearch(e.target.value)}
          title={t("pages.myCoupons")}
          buttonTitle={t("actions.addCoupon")}
          children={
            <>
              <FiltersButton
                onClick={() => {
                  setDraftCouponStatusFilter(couponStatusFilter);
                  setDraftCouponTypeFilter(couponTypeFilter);
                  setDraftCategoryTypeFilter(categoryTypeFilter);
                  setDraftRecurringDaysFilter(recurringDaysFilter);
                  setDraftSortFilter(sortFilter);
                  setFiltersDialogOpen(true);
                }}
                activeCount={
                  (couponStatusFilter !== "" ? 1 : 0) +
                  (couponTypeFilter !== "" ? 1 : 0) +
                  (categoryTypeFilter !== "" ? 1 : 0) +
                  recurringDaysFilter.length +
                  (sortFilter ? 1 : 0)
                }
              />
            </>
          }
        />
      </ResponsiveProvider>
      <FiltersDialog
        open={filtersDialogOpen}
        onClose={() => setFiltersDialogOpen(false)}
        onReset={() => {
          setDraftCouponStatusFilter("");
          setCouponStatusFilter("");
          setDraftCouponTypeFilter("");
          setCouponTypeFilter("");
          setDraftCategoryTypeFilter("");
          setCategoryTypeFilter("");
          setDraftRecurringDaysFilter([]);
          setRecurringDaysFilter([]);
          setDraftSortFilter("");
          setSortFilter("");
          setFiltersDialogOpen(false);
        }}
        onApply={() => {
          setCouponStatusFilter(draftCouponStatusFilter);
          setCouponTypeFilter(draftCouponTypeFilter);
          setCategoryTypeFilter(draftCategoryTypeFilter);
          setRecurringDaysFilter(draftRecurringDaysFilter);
          setSortFilter(draftSortFilter);
          setFiltersDialogOpen(false);
        }}
        disableApply={!hasPendingChanges}
      >
        <FilterChipGroup
          label={t("filters.allStates")}
          options={CouponStatusFilterList}
          value={draftCouponStatusFilter}
          onChange={setDraftCouponStatusFilter}
          allowAll
        />
        <FilterChipGroup
          label={t("filters.allTypes")}
          options={CouponTypeList}
          value={draftCouponTypeFilter}
          onChange={setDraftCouponTypeFilter}
          allowAll
        />
        <FilterChipGroup
          label={t("fields.targetType")}
          options={CategoryTypeFilterOptions}
          value={draftCategoryTypeFilter}
          onChange={setDraftCategoryTypeFilter}
          allowAll
        />
        <FilterChipGroup
          label={t("table.recurringDays")}
          options={RecurringDaysFilterOptions}
          value={draftRecurringDaysFilter}
          onChange={setDraftRecurringDaysFilter}
          multiple
        />
        <FilterChipGroup
          label={t("filters.sortBy")}
          options={CouponSortList}
          value={draftSortFilter}
          onChange={setDraftSortFilter}
        />
      </FiltersDialog>
      <main>
        <Box sx={{ my: 2, minWidth: 0 }}>
          <TableContainer>
            <Table
              sx={{
                backgroundColor: colors.primary[400],
              }}
            >
              <TableHead
                sx={{
                  backgroundColor: colors.primary[700],
                }}
              >
                <TableRow>
                  <TableCell>{t("table.code")}</TableCell>
                  <TableCell>{t("table.type")}</TableCell>
                  <TableCell>{t("table.value")}</TableCell>
                  <TableCell>{t("table.minValue")}</TableCell>
                  <TableCell>{t("table.limit")}</TableCell>
                  <TableCell>{t("table.usageCount")}</TableCell>
                  <TableCell>{t("table.status")}</TableCell>
                  <TableCell>{t("table.category")}</TableCell>
                  <TableCell>{t("table.product")}</TableCell>
                  <TableCell>{t("table.excludedProducts")}</TableCell>
                  <TableCell>{t("table.createdAt")}</TableCell>
                  <TableCell>{t("table.expiresAt")}</TableCell>
                  <TableCell>{t("table.recurringDays")}</TableCell>
                  <TableCell align="right">{t("table.actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {status === "loadingFetchData" ? (
                  <TableRow>
                    <TableCell colSpan={14} align="center">
                      <Loading size={8} />
                    </TableCell>
                  </TableRow>
                ) : status === "fetchError" ? (
                  <TableRow>
                    <TableCell colSpan={14} align="center">
                      <Error>{error}</Error>
                    </TableCell>
                  </TableRow>
                ) : filteredCouponsMemo && filteredCouponsMemo.length > 0 ? (
                  filteredCouponsMemo.map((card) => (
                    <TableRow key={card._id}>
                      <TableCell>
                        <ParagraphHighliter title={card.code} search={search} />
                      </TableCell>
                      <TableCell>{getCouponLabel(card.couponType)}</TableCell>
                      <TableCell>{card.couponValue}</TableCell>
                      <TableCell>{card.minOrderAmount}</TableCell>
                      <TableCell>{card.limit}</TableCell>
                      <TableCell>{card.usageCount}</TableCell>
                      <TableCell>
                        <Chip
                          variant="outlined"
                          label={t(getCouponStatusMeta(card).labelKey)}
                          color={getCouponStatusMeta(card).color}
                        />
                      </TableCell>
                      <TableCell>
                        {card.categoryType === "all" ? (
                          <Chip variant="outlined" label="—" color="default" />
                        ) : card.categoryType === "categories" ||
                          card.categoryType === "categories_products" ? (
                          card.couponCategories &&
                            card.couponCategories.length > 0 ? (
                            <ShowMultiItems
                              items={card.couponCategories}
                              useStyledTypography={false}
                            />
                          ) : (
                            <Chip
                              variant="outlined"
                              label={t("fields.noCategory")}
                              color="default"
                            />
                          )
                        ) : (
                          <></>
                        )}
                      </TableCell>
                      <TableCell>
                        {card.categoryType === "all" ? (
                          <Chip variant="outlined" label="—" color="default" />
                        ) : card.categoryType === "products" ||
                          card.categoryType === "categories_products" ? (
                          card.couponProducts &&
                            card.couponProducts.length > 0 ? (
                            <ShowMultiItems
                              items={card.couponProducts}
                              useStyledTypography={false}
                            />
                          ) : (
                            <Chip
                              variant="outlined"
                              label={t("fields.noProduct")}
                              color="default"
                            />
                          )
                        ) : (
                          <></>
                        )}
                      </TableCell>
                      <TableCell>
                        {card.excludeProducts &&
                          card.excludeProducts.length > 0 ? (
                          <ShowMultiItems
                            items={card.excludeProducts}
                            useStyledTypography={false}
                          />
                        ) : (
                          ""
                        )}
                      </TableCell>
                      <TableCell>
                        {card.startDate ? formatDateTime(card.startDate) : "—"}
                      </TableCell>
                      <TableCell>
                        {card.endDate ? formatDateTime(card.endDate) : "—"}
                      </TableCell>
                      <TableCell>
                        {card.recurringDays && card.recurringDays.length > 0 ? (
                          <ShowMultiItems
                            items={formatRecurringDaysForDisplay(card.recurringDays)}
                            useStyledTypography={false}
                          />
                        ) : (
                          ""
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                        <TableActions
                          compact
                          actions={[
                            {
                              type: card.isActive ? "block" : "activate",
                              color: card.isActive ? "success" : "error",
                              label: card.isActive
                                ? t("common.deactivateCoupon")
                                : t("common.activateCoupon"),
                              onClick: () => {
                                setActive(true);
                                setCardId(card._id);
                                setActiveType(card.isActive);
                              },
                            },
                            {
                              type: "edit",
                              color: "warning",
                              label: t("common.edit"),
                              onClick: () => handleModify(card),
                            },
                            {
                              type: "delete",
                              color: "error",
                              label: t("common.delete"),
                              onClick: () => handleClickOpen(card._id),
                            },
                          ]}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <NoDataTable cols={14} />
                )}
              </TableBody>
            </Table>
            <AlertDialog
              handleClose={() => setOpen(false)}
              open={open}
              message={"Voulez-vous supprimer ce coupon ?"}
              cardId={cardId}
              deleteData={() =>
                dispatch(deleteCoupon({ couponId: cardId, restaurantId }))
              }
            />
            <AlertDialog
              handleClose={() => setActive(false)}
              open={active}
              title={activeType ? t("common.deactivate") : t("common.activate")}
              message={t("common.toggleCoupon", {
                action: activeType ? t("common.deactivate").toLowerCase() : t("common.activate").toLowerCase(),
              })}
              cardId={cardId}
              deleteData={() =>
                dispatch(toggleCoupon({ couponId: cardId, restaurantId }))
              }
            />
          </TableContainer>
        </Box>
      </main>
    </div>
  );
};

export default CouponManagement;
